import { ExpandMore } from '@/constants/hardfin-standard-icons';
import { ProjectFilter } from '@/project/api/get-projects';
import { ProjectBillingCurrencySection } from '@/project/components/project-billing-currency-section';
import { useProjectBillingAggregate } from '@/project/hooks/use-project-billing-aggregate';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';

/**
 * Props for the ProjectsFinancialSummary component
 */
interface Props {
  /**
   * Filter criteria to determine which projects to include in the summary.
   */
  filter?: Omit<ProjectFilter, 'pageSize' | 'pageNumber' | 'sort'>;
}

/**
 * A component that displays financial summary aggregated across filtered projects.
 * Uses controlled state to preserve accordion expansion across filter changes.
 */
export function ProjectsFinancialSummary(props: Props) {
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    billingData: financialData,
    isLoading: isBillingDataLoading,
    isFetching: isBillingDataFetching,
  } = useProjectBillingAggregate(props.filter);

  // No charges at all means no currency groups; a currency only appears when it
  // has charges (its own section then handles zero-priced charges).
  const hasNoCharges = financialData.byCurrency.length === 0;

  // Show loading state when initial load OR when refetching with no previous data
  const showLoadingIndicator = isBillingDataLoading || isBillingDataFetching;

  /**
   * Handles accordion expansion toggle.
   */
  const handleAccordionChange = (
    _event: React.SyntheticEvent,
    expanded: boolean
  ) => {
    setIsExpanded(expanded);
  };

  return (
    <Box sx={{ position: 'relative', minHeight: 56, marginBottom: 2 }}>
      {showLoadingIndicator && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1,
            borderRadius: '4px 4px 0 0',
          }}
        />
      )}
      <Accordion
        disableGutters
        elevation={0}
        expanded={isExpanded}
        onChange={handleAccordionChange}
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          '&:before': {
            display: 'none',
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="billing-summary-content"
          id="billing-summary-header"
          sx={{
            minHeight: 56,
            '& .MuiAccordionSummary-content': {
              margin: '12px 0',
            },
          }}
        >
          <Typography variant="subtitle1">Billing summary</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          <Box sx={{ minHeight: 215 }}>
            {isBillingDataLoading ? (
              <Box sx={{ minHeight: 215 }}>
                <Box padding={2} paddingBottom={1}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Skeleton variant="rectangular" height={40} />
                    </Box>
                    <Skeleton variant="text" width={100} height={40} />
                  </Stack>
                </Box>

                <Grid container spacing={2} padding={2} paddingTop={1}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper variant="outlined" sx={{ px: 2.5, py: 1.5 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        mb={1}
                      >
                        <Skeleton variant="circular" width={12} height={12} />
                        <Skeleton variant="text" width={100} />
                      </Stack>
                      <Skeleton variant="text" width={80} height={32} />
                      <Skeleton variant="text" width={60} />
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Paper variant="outlined" sx={{ px: 2.5, py: 1.5 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        mb={1}
                      >
                        <Skeleton variant="circular" width={12} height={12} />
                        <Skeleton variant="text" width={100} />
                      </Stack>
                      <Skeleton variant="text" width={80} height={32} />
                      <Skeleton variant="text" width={60} />
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Paper variant="outlined" sx={{ px: 2.5, py: 1.5 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        mb={1}
                      >
                        <Skeleton variant="circular" width={12} height={12} />
                        <Skeleton variant="text" width={100} />
                      </Stack>
                      <Skeleton variant="text" width={80} height={32} />
                      <Skeleton variant="text" width={60} />
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Paper variant="outlined" sx={{ px: 2.5, py: 1.5 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        mb={1}
                      >
                        <Skeleton variant="circular" width={12} height={12} />
                        <Skeleton variant="text" width={100} />
                      </Stack>
                      <Skeleton variant="text" width={80} height={32} />
                      <Skeleton variant="text" width={60} />
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            ) : hasNoCharges ? (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                minHeight={215}
              >
                <Typography color="text.secondary" textAlign="center">
                  No financial data available for the selected projects
                </Typography>
              </Box>
            ) : (
              <Stack divider={<Divider />}>
                {financialData.byCurrency.map((datum) => (
                  <ProjectBillingCurrencySection
                    key={datum.isoCode}
                    datum={datum}
                  />
                ))}
              </Stack>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
